/**
 * The licensing-path contract, client side (#411).
 *
 * MIRROR, NOT A GUESS: every type here is the shape aswb-backend already
 * publishes in `src/contracts/licensing.ts` + `src/licensing/rulebook.ts`. The
 * client derives NOTHING from the record — `firstSentence`, `nextStep`,
 * `examRequiredFirst`, `headlineKind` and `knownFeesUsd` are computed once,
 * server-side, so web and iOS cannot disagree about whether a learner's state
 * needs the exam first.
 *
 * The record itself is kept LOOSE (optional fields, index signature) for the
 * same reason the backend keeps it loose: the rulebook data is still growing
 * (#106) and a strict mirror here would turn a data addition into a web deploy.
 */

/** USPS codes for the 50 states + DC. Territories are absent on purpose (no verified data). */
export type Jurisdiction = string;

export type LicenseRung =
  | 'bachelors'
  | 'masters'
  | 'limited'
  | 'supervisee'
  | 'advanced'
  | 'clinical';

export type ProcessStage =
  | 'not_applied'
  | 'waiting'
  | 'approved'
  | 'scheduled'
  | 'retaking'
  | 'passed';

/**
 * WHAT KIND of headline the card is showing (backend #316/#318). Clients
 * branch on THIS, never on the copy: web used to detect the passed stage by
 * inspecting `firstSentence`, and this enum is what that cost.
 *
 * The first three are stage-independent and track `examRequiredFirst`
 * (`exam_required` <=> true). The last three are stage facts and say nothing
 * about whether the exam gates the first license.
 */
export type HeadlineKind =
  | 'exam_required'
  | 'exam_optional'
  | 'limited_first'
  | 'passed'
  | 'retaking'
  | 'scheduled';

export type Employment = 'working_sw' | 'starting_soon' | 'not_working';

export type YesNoUnsure = 'yes' | 'no' | 'unsure';
export type YesNoUnknown = 'yes' | 'no' | 'unknown';

/**
 * The persisted licensing fields, in the step's render order.
 *
 * THE SHARED CONSTANT the drift test pins: this list is the same list as
 * aswb-backend `src/licensing/profileFields.ts`, and `UserService`'s
 * hand-maintained request interfaces type their licensing half off it, so a
 * field added in one place cannot silently be dropped in the other.
 */
export const LICENSING_PROFILE_FIELDS = [
  'jurisdiction',
  'licenseRung',
  'processStage',
  'employment',
  'employerSupervision',
  'clinicalIntent',
  'supervisorVerified',
] as const;

export type LicensingProfileField = (typeof LICENSING_PROFILE_FIELDS)[number];

/** The answers as they are collected and as they are written. Every field optional. */
export interface LicensingAnswers {
  jurisdiction?: Jurisdiction;
  licenseRung?: LicenseRung;
  processStage?: ProcessStage;
  employment?: Employment;
  employerSupervision?: YesNoUnsure;
  clinicalIntent?: YesNoUnsure;
  supervisorVerified?: YesNoUnknown;
}

/** Compile-time proof that LicensingAnswers covers exactly the shared field list. */
export type LicensingProfilePayload = { [K in LicensingProfileField]?: string };

export interface RulebookFee {
  name: string;
  /** null when the board publishes no amount. NEVER 0. */
  amount_usd: number | null;
  /** An ASWB exam fee that depends on the level (amount_usd is then null); never in the fixed total. */
  amount_by_level?: Record<string, number>;
  source_url?: string | null;
  verified_date?: string | null;
}

export interface RulebookSource {
  label: string;
  url: string;
}

export interface RulebookRecord {
  state: { name: string; postal_code: string };
  license_target: { track: string; title: string; abbreviation: string };
  board?: { name?: string | null; source_url?: string | null } | null;
  fees: RulebookFee[];
  application_sequence: string[];
  supervision_requirement?: {
    experience_hours?: { value?: number | null } | null;
    client_contact_hours?: { value?: number | null } | null;
    [key: string]: unknown;
  } | null;
  last_verified?: string | null;
  sources?: RulebookSource[];
  [key: string]: unknown;
}

export interface LadderRung {
  rung: LicenseRung;
  abbr: string;
  title: string;
  /** ASWB category required to HOLD the rung. null = no exam. */
  exam: string | null;
  supervised: boolean;
  source?: string;
  /** Who picks when `exam` lists several levels; absent when the record does not say. */
  examChoice?: 'board' | 'applicant';
  /** false: no record of this rung's own has been read, so never borrow another license's. */
  checked?: boolean;
  /** 'unconfirmed': the rule names only "an examination approved by the board", so never assert ASWB. */
  examProvider?: 'unconfirmed';
}

export interface LicensingPathUnavailable {
  available: false;
  jurisdiction: string;
  /**
   * `no_data`: a real state whose board research is not done yet (#106).
   * `rung_not_offered`: the state does not have that rung (MI has no plain
   * masters rung). Two different facts, two different cards, neither an error.
   */
  reason: 'no_data' | 'rung_not_offered';
  offeredRungs: LicenseRung[];
}

export interface LicensingPathAvailable {
  available: true;
  jurisdiction: string;
  licenseRung: LicenseRung;
  record: RulebookRecord;
  records: RulebookRecord[];
  ladder: LadderRung[];
  /** null when processStage was not supplied. A guessed next step is worse than none. */
  nextStep: { stage: ProcessStage; text: string } | null;
  /** Sum of PUBLISHED fees only. null, never 0. */
  knownFeesUsd: number | null;
  unknownFeeCount: number;
  examRequiredFirst: boolean;
  /**
   * Optional so this type still fits an older backend response: absent means
   * the deploy that stopped guessing from the sentence has not reached this
   * client's backend yet, and every branch below falls back to
   * `examRequiredFirst`.
   */
  headlineKind?: HeadlineKind;
  /** Stage-aware since #318: withheld-for-passed on the client is now obsolete. */
  firstSentence: string;
  lastVerified: string | null;
}

export type LicensingPath = LicensingPathAvailable | LicensingPathUnavailable;

export function isAvailable(path: LicensingPath | null | undefined): path is LicensingPathAvailable {
  return !!path && path.available;
}
